import { Modal, Select, Checkbox, Button, Alert, message } from 'antd';
import { useState } from 'react';
import { splitApplicationFromGroupService } from '../../../../services/easyapp.service';
import styles from './group-data.module.scss';

interface SplitApplicationModalProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
  applicants: any[];
  applicationGroupId: string;
}

const SplitApplicationModal = ({
  visible,
  onClose,
  onSuccess,
  applicants,
  applicationGroupId,
}: SplitApplicationModalProps) => {
  const [selectedApplicationId, setSelectedApplicationId] = useState<string | null>(null);
  const [archiveAfterSplit, setArchiveAfterSplit] = useState(true);
  const [loading, setLoading] = useState(false);

  const handleSplit = async () => {
    if (!selectedApplicationId) {
      message.error('Please select an applicant to split');
      return;
    }

    try {
      setLoading(true);
      const response: any = await splitApplicationFromGroupService({
        sourceGroupId: applicationGroupId,
        applicationId: selectedApplicationId,
        archive: archiveAfterSplit,
      });

      if (response?.data?.success) {
        const applicantName = response.data.applicantName || 'Applicant';
        message.success(
          archiveAfterSplit
            ? `${applicantName} has been split from the group and archived`
            : `${applicantName} has been split into a new application group`
        );
        // Reset state
        setSelectedApplicationId(null);
        setArchiveAfterSplit(true);
        onSuccess();
        onClose();
      } else {
        message.error(response?.data?.message || 'Failed to split application');
      }
    } catch (error: any) {
      console.error('Error splitting application:', error);
      message.error(error?.response?.data?.message || 'Failed to split application');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedApplicationId(null);
    setArchiveAfterSplit(true);
    onClose();
  };

  const applicantOptions = applicants
    ?.filter((a) => a?.application_id)
    .map((applicant) => ({
      value: applicant.application_id,
      label: `${applicant.first_name || ''} ${applicant.last_name || ''}`.trim() || 'Unknown',
    }));

  const canSplit = applicants?.length > 1;

  const selectedApplicant = applicants?.find(
    (a) => a?.application_id === selectedApplicationId
  );
  const selectedApplicantName = selectedApplicant
    ? `${selectedApplicant.first_name || ''} ${selectedApplicant.last_name || ''}`.trim()
    : null;

  return (
    <Modal
      title="Split Applicant from Group"
      open={visible}
      onCancel={handleClose}
      footer={[
        <Button key="cancel" onClick={handleClose}>
          Cancel
        </Button>,
        <Button
          key="split"
          type="primary"
          loading={loading}
          onClick={handleSplit}
          disabled={!selectedApplicationId || !canSplit}
          danger={archiveAfterSplit}
        >
          {archiveAfterSplit ? 'Split & Archive' : 'Split to New Group'}
        </Button>,
      ]}
      width={520}
    >
      {!canSplit ? (
        <Alert
          type="warning"
          message="Cannot Split"
          description="This application group only has one applicant. Splitting requires at least 2 applicants in the group."
          showIcon
        />
      ) : (
        <>
          <Alert
            type="info"
            message="About Splitting"
            description="Splitting will move the selected applicant into their own new application group. This is useful when a co-applicant decides not to proceed or needs to be processed separately."
            showIcon
            style={{ marginBottom: 20 }}
          />

          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
              Select applicant to split:
            </label>
            <Select
              style={{ width: '100%' }}
              placeholder="Choose an applicant"
              options={applicantOptions}
              value={selectedApplicationId}
              onChange={setSelectedApplicationId}
              size="large"
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <Checkbox
              checked={archiveAfterSplit}
              onChange={(e) => setArchiveAfterSplit(e.target.checked)}
            >
              <span style={{ fontWeight: 500 }}>Archive after splitting</span>
            </Checkbox>
          </div>

          <div
            style={{
              padding: 16,
              background: archiveAfterSplit ? '#fff7e6' : '#e6f7ff',
              border: `1px solid ${archiveAfterSplit ? '#ffd591' : '#91d5ff'}`,
              borderRadius: 6,
            }}
          >
            {archiveAfterSplit ? (
              <>
                <p style={{ margin: 0, fontWeight: 500, color: '#d46b08' }}>
                  📦 Archive Mode
                </p>
                <p style={{ margin: '8px 0 0 0', fontSize: 13, color: '#8c8c8c' }}>
                  {selectedApplicantName ? (
                    <>
                      <strong>{selectedApplicantName}</strong> will be moved to "Past
                      Applications" and marked as no longer proceeding.
                    </>
                  ) : (
                    <>
                      The selected applicant will be moved to "Past Applications" and
                      marked as no longer proceeding.
                    </>
                  )}
                  <br />
                  <em>Use this when they've decided not to move in.</em>
                </p>
              </>
            ) : (
              <>
                <p style={{ margin: 0, fontWeight: 500, color: '#096dd9' }}>
                  📋 Keep Active Mode
                </p>
                <p style={{ margin: '8px 0 0 0', fontSize: 13, color: '#8c8c8c' }}>
                  {selectedApplicantName ? (
                    <>
                      <strong>{selectedApplicantName}</strong> will remain in "Current
                      Applications" as a standalone applicant.
                    </>
                  ) : (
                    <>
                      The selected applicant will remain in "Current Applications" as
                      a standalone applicant.
                    </>
                  )}
                  <br />
                  <em>Use this when they want to continue applying separately.</em>
                </p>
              </>
            )}
          </div>

          {selectedApplicationId && applicants?.length === 2 && (
            <div
              style={{
                marginTop: 16,
                padding: 12,
                background: '#f5f5f5',
                borderRadius: 6,
                fontSize: 13,
              }}
            >
              <strong>After splitting:</strong>
              <ul style={{ margin: '8px 0 0 0', paddingLeft: 20 }}>
                <li>
                  {applicants.find((a) => a.application_id !== selectedApplicationId)?.first_name ||
                    'The other applicant'}{' '}
                  will remain in the current group
                </li>
                <li>
                  {selectedApplicantName || 'The selected applicant'} will be in a new
                  {archiveAfterSplit ? ' archived' : ''} group
                </li>
              </ul>
            </div>
          )}
        </>
      )}
    </Modal>
  );
};

export default SplitApplicationModal;

